'use client';

import React, { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';

// PanelContext manages panel CONTENT only.
// Panel VISIBILITY is controlled by SidebarProvider (useSidebar open/rightOpen).
interface PanelContextType {
  leftPanel: ReactNode | null;
  setLeftPanel: (content: ReactNode | null) => void;
  rightPanel: ReactNode | null;
  setRightPanel: (content: ReactNode | null) => void;
  /** Nav menu content injected above leftPanel (for responsive nav items) */
  navMenu: ReactNode | null;
  setNavMenu: (content: ReactNode | null) => void;
}

const PanelContext = createContext<PanelContextType | undefined>(undefined);

export function PanelProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname() ?? '';
  const [leftPanel, setLeftPanel] = useState<ReactNode | null>(null);
  const [rightPanel, setRightPanel] = useState<ReactNode | null>(null);
  const [navMenu, setNavMenu] = useState<ReactNode | null>(null);
  const previousPathnameRef = useRef<string | null>(null);

  // Track pathname changes for debugging/future use
  // Note: Panel cleanup is handled by VaultLayout's useLayoutEffect cleanup function.
  // Previously this useEffect cleared panels on vault change, but that caused a race
  // condition where useEffect (runs after paint) would clear panels that VaultLayout's
  // useLayoutEffect (runs before paint) had just set.
  useEffect(() => {
    previousPathnameRef.current = pathname;
  }, [pathname]);

  return (
    <PanelContext.Provider
      value={{
        leftPanel,
        setLeftPanel,
        rightPanel,
        setRightPanel,
        navMenu,
        setNavMenu,
      }}>
      {children}
    </PanelContext.Provider>
  );
}

export function usePanel() {
  const context = useContext(PanelContext);
  if (context === undefined) {
    throw new Error('usePanel must be used within a PanelProvider');
  }
  return context;
}

/** Optional hook that returns null if outside provider (for components that may be used outside app shell) */
export function usePanelOptional() {
  return useContext(PanelContext);
}
